export const equipmentCategories = [
  "HVAC",
  "Electrical",
  "Plumbing",
  "Mechanical",
  "Refrigeration",
  "Fire Alarm",
  "Kitchen Equipment",
  "Other",
];

export const contactTypes = [
  "Engineering",
  "Billing",
  "Maintenance",
  "Account Manager",
  "Security",
  "Other",
];

export const serviceTagOptions = [
  "Maintenance",
  "Service Call",
  "Completed Repair",
];

// Billing/classification type for a job (work order).
export const jobTypeOptions = [
  "Service Call",
  "T&M",
  "Maintenance",
  "Quoted",
  "Warranty",
];

// Roles available per organization type. Mechanical (contractor) orgs use
// admin/manager/technician; facility (customer) orgs use executive/
// facilities_manager/maintenance. Used by Owner Panel + Manage Users.
export const rolesByOrgType = {
  mechanical: ["admin", "manager", "technician"],
  facility: ["executive", "facilities_manager", "maintenance"],
};

export const roleLabels = {
  admin: "Admin",
  manager: "Manager",
  technician: "Technician",
  executive: "Executive",
  facilities_manager: "Facilities Manager",
  maintenance: "Maintenance",
};

// Per-user capabilities an admin can grant to a technician, layered on
// top of the base role. Admin/manager already have all of these via
// their role; these toggles only matter for technicians.
export const capabilityOptions = [
  { key: "addCustomers", label: "Add Customers" },
  { key: "editCustomers", label: "Edit Customers" },
  { key: "deleteCustomers", label: "Delete Customers" },
  { key: "addLocations", label: "Add Locations" },
  { key: "editLocations", label: "Edit Locations" },
  { key: "deleteLocations", label: "Delete Locations" },
  { key: "addEquipment", label: "Add Equipment" },
  { key: "editEquipment", label: "Edit Equipment" },
  { key: "deleteEquipment", label: "Delete Equipment" },
  { key: "createJobs", label: "Create Jobs" },
  { key: "editJobs", label: "Edit Jobs" },
  { key: "viewEstimatedBill", label: "View Estimated Bill (rates, costs, margins)" },
  { key: "deleteJobs", label: "Delete Jobs" },
  { key: "createQuotes", label: "Create Quotes" },
  { key: "createPOs", label: "Create Purchase Orders" },
  { key: "manageVendors", label: "Manage Vendors (add / edit / retire)" },
  { key: "verifyVendorBills", label: "Verify Vendor Bills (AP reconciliation)" },
  { key: "voidBills", label: "Void Invoices / Bills (AR)" },
  { key: "markPaid", label: "Record Customer Payments (AR mark-paid)" },
  { key: "approvePayment", label: "Approve Vendor Bills for Payment (AP)" },
  { key: "viewARAging", label: "View AR Aging (past-due buckets)" },
  { key: "viewAPAging", label: "View AP Aging (vendor bills due)" },
  { key: "viewPaymentHistory", label: "View Payment History (customer rollups)" },
  { key: "viewMargin", label: "View Margin Reports (P&L by customer / job type / month)" },
  { key: "viewInvoices", label: "View Invoices (open invoice list / re-export)" },
  { key: "viewClosedBills", label: "View Closed Bills (archive of finalized invoices)" },
  // orgType-scoped caps — only render on the matching org type's
  // Manage Users page. Without this filter a contractor admin would
  // see facility-only caps in their grid (Justin caught this 2026-06-15).
  { key: "requestService", label: "Request Service", orgType: "facility" },
  { key: "viewSignedTickets", label: "View Signed Visits", orgType: "facility" },
  { key: "deleteSignedTickets", label: "Delete Signed Tickets", orgType: "facility" },
  { key: "approveTime", label: "Approve / Lock Time Entries (payroll)" },
  // Scope-of-view capabilities. Default-off for tech tier so a tech only
  // sees the quotes / POs / service tickets they created themselves.
  // Admins / managers see everything by default (their tier grants it).
  { key: "viewAllQuotes", label: "View All Quotes (not just own)" },
  { key: "viewAllPOs", label: "View All POs (not just own)" },
  { key: "viewAllServiceTickets", label: "View All Service Tickets (not just own)" },
  { key: "viewRecentActivity", label: "View Recent Activity Dashboard" },
];

// Notification event types — each user can toggle which ones land in their
// alerts panel via Notification Settings. Defaults (per role tier) follow
// hasCapability's convention: admin always on, manager default-on, tech
// default-off (must explicitly opt in).
//
// `availableTo` lists the roles that can subscribe to an event. Techs are
// deliberately restricted to the three events they actually need to act on:
// service requests they can volunteer for, jobs assigned TO them, and the
// approval of THEIR OWN PO request. Wider org-noise events stay admin/
// manager-only.
const ALL_ROLES = [
  "admin",
  "executive",
  "manager",
  "facilities_manager",
  "technician",
  "maintenance",
];
const ADMIN_MANAGER_ROLES = [
  "admin",
  "executive",
  "manager",
  "facilities_manager",
];

export const notificationEvents = [
  {
    key: "recent_activity",
    label: "Recent activity (service records)",
    description:
      "When a service record is logged on equipment — contractor-side for your techs' work, facility-side for your contractor's work at your buildings.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "ticket_signed",
    label: "Signed work order (from contractor)",
    description: "Facility-side: when your contractor signs a service ticket on your building.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "ticket_signed_internal",
    label: "Ticket signed (in your org)",
    description: "Contractor-side: when one of your techs signs a service ticket — kicks off billing.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "invoice_ready",
    label: "Invoice ready",
    description: "When your contractor finalizes a bill.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "service_requested",
    label: "New service request",
    description: "Contractor-side: a facility requested service on a piece of equipment.",
    availableTo: ALL_ROLES,
  },
  {
    key: "service_accepted",
    label: "Service request accepted",
    description: "Facility-side: your contractor accepted a service request.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "service_declined",
    label: "Service request declined",
    description: "Facility-side: your contractor declined a service request.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "equipment_status",
    label: "Equipment health changed",
    description: "Contractor-side: a linked facility's equipment went yellow (caution) or red (disabled). Requires the facility be opted in on Connections.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "job_status_changed",
    label: "Job status changed",
    description: "When a job transitions between unassigned / in progress / completed.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "job_assigned_to_me",
    label: "Job assigned to me",
    description: "When you're added to a job's assigned crew.",
    availableTo: ALL_ROLES,
  },
  {
    key: "po_requested",
    label: "PO request submitted",
    description: "A tech submitted a PO request waiting for approval.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "po_ordered",
    label: "PO ordered",
    description: "A PO has been placed with a vendor.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "po_received",
    label: "PO received",
    description: "Parts arrived — PO marked received.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "po_request_approved",
    label: "My PO request approved",
    description: "When the PO you requested gets placed with a vendor.",
    availableTo: ALL_ROLES,
  },
  {
    key: "new_message",
    label: "New message",
    description: "When the other side sends a message in a thread you can see.",
    availableTo: ALL_ROLES,
  },
  {
    key: "quote_sent",
    label: "New quote received",
    description: "Facility-side: your contractor sent you a new quote to review.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "quote_approved",
    label: "Quote approved",
    description: "Contractor-side: a facility approved one of your quotes; the linked job is created.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "quote_requested",
    label: "Quote requested",
    description: "Contractor-side: a facility requested a quote on a building/equipment.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "pm_overdue",
    label: "PM overdue",
    description: "Preventive maintenance is past due on a piece of equipment.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
  {
    key: "pm_due_soon",
    label: "PM due soon",
    description: "Preventive maintenance is due within 30 days.",
    availableTo: ADMIN_MANAGER_ROLES,
  },
];

export const equipmentSubtypes = {
  HVAC: [
    "Air Handler (AHU)",
    "Rooftop Unit (RTU)",
    "Split System",
    "Mini-Split / Ductless",
    "Heat Pump",
    "Chiller (Air-Cooled)",
    "Chiller (Water-Cooled)",
    "Cooling Tower",
    "Boiler (Hot Water)",
    "Boiler (Steam)",
    "Furnace",
    "Fan Coil Unit (FCU)",
    "VAV Box",
    "Fan-Powered Box (FPB)",
    "Exhaust Fan",
    "Make-Up Air Unit",
    "Energy Recovery Ventilator (ERV)",
    "Heat Recovery Ventilator (HRV)",
    "Humidifier",
    "Dehumidifier",
    "Computer Room AC (CRAC/CRAH)",
    "Pump (Chilled Water)",
    "Pump (Hot Water)",
    "Pump (Condenser Water)",
    "VFD",
    "Motor Starter Panel",
    "Unit Heater",
    "Duct Heater",
    "Thermostat / Controls",
    "Other",
  ],
  Electrical: [
    "VFD",
    "Motor Control Center (MCC)",
    "Motor Starter Panel",
    "Switchgear (Low Voltage)",
    "Switchgear (Medium Voltage)",
    "Generator",
    "Transformer",
    "UPS",
    "Automatic Transfer Switch (ATS)",
    "Panelboard",
    "Switchboard",
    "Bus Duct",
    "Surge Protective Device (SPD)",
    "Power Distribution Unit (PDU)",
    "Disconnect Switch",
    "Capacitor Bank",
    "Other",
  ],
  Plumbing: [
    "Water Heater (Tank)",
    "Water Heater (Tankless)",
    "Booster Pump",
    "Backflow Preventer (RPZ)",
    "Water Softener",
    "Sump Pump",
    "Ejector / Lift Station Pump",
    "Hot Water Recirculation Pump",
    "Storage Tank",
    "Expansion Tank",
    "Water Treatment / Filtration",
    "Grease Interceptor",
    "Sewage Pump",
    "Other",
  ],
  Mechanical: [
    "Air Compressor",
    "Air Dryer",
    "Air Receiver Tank",
    "Hydraulic Pump",
    "Conveyor",
    "Pump (General)",
    "Motor (General)",
    "Gearbox / Reducer",
    "Other",
  ],
  Refrigeration: [
    "Walk-In Cooler",
    "Walk-In Freezer",
    "Reach-In Cooler",
    "Reach-In Freezer",
    "Ice Machine",
    "Refrigerated Display Case",
    "Condensing Unit",
    "Evaporator Coil",
    "Refrigeration Rack",
    "Other",
  ],
  "Fire Alarm": [
    "Fire Alarm Control Panel (FACP)",
    "Smoke Detector",
    "Heat Detector",
    "Pull Station",
    "Horn/Strobe Notification",
    "Sprinkler System",
    "Fire Pump",
    "Standpipe / Riser",
    "Annunciator Panel",
    "Other",
  ],
  "Kitchen Equipment": [
    "Hood Exhaust System",
    "Make-Up Air Unit (Kitchen)",
    "Ansul / Suppression System",
    "Ice Machine",
    "Commercial Dishwasher",
    "Range / Stove",
    "Convection Oven",
    "Combi Oven",
    "Steam Kettle",
    "Steamer",
    "Fryer",
    "Griddle",
    "Salamander / Broiler",
    "Walk-In Cooler",
    "Walk-In Freezer",
    "Reach-In Refrigerator",
    "Reach-In Freezer",
    "Other",
  ],
  Other: [],
};
